import { distanceMeters } from "../../functions/src/locations/trailCleanup";

/**
 * Decides which GPS fixes are worth saving. Kept in step with the native
 * `LocationSampler` in the Wear OS repo (core module), which the phone and
 * watch apps use for the same job.
 *
 * - Fixes worse than [MAX_ACCURACY_M] are dropped — those are the ones that put
 *   you a block away and then snap back.
 * - Fixes older than [MAX_FIX_AGE_MS] are dropped: a cached fix stamped with
 *   the current time draws a line to wherever you were before.
 * - While moving, a fix is saved every [MIN_INTERVAL_MS] once you're
 *   [MIN_DISTANCE_M] (or the fix's own error, if bigger) from the last one —
 *   enough to follow a footpath.
 * - Standing still, one fix every [HEARTBEAT_MS], so the replay knows you were
 *   still there rather than missing.
 */
export const MAX_ACCURACY_M = 65;
export const MAX_FIX_AGE_MS = 2 * 60_000;
export const MIN_INTERVAL_MS = 10_000;
export const MIN_DISTANCE_M = 10;
export const HEARTBEAT_MS = 5 * 60_000;

export interface Fix {
  lat: number;
  lng: number;
  /** Radius of 68% confidence, metres; null if the platform didn't say. */
  accuracyM: number | null;
  /** When the fix was taken, epoch ms. */
  t: number;
}

export interface LocationSampler {
  /** True if `fix` should be saved; remembers it as the last saved fix if so. */
  accept(fix: Fix, nowMs: number): boolean;
  reset(): void;
}

export function createLocationSampler(): LocationSampler {
  let last: Fix | null = null;
  return {
    accept(fix, nowMs) {
      if (fix.accuracyM != null && fix.accuracyM > MAX_ACCURACY_M) return false;
      if (nowMs - fix.t > MAX_FIX_AGE_MS) return false;
      if (last) {
        const dt = fix.t - last.t;
        if (dt < MIN_INTERVAL_MS) return false;
        const moved = distanceMeters(last, fix) >= Math.max(MIN_DISTANCE_M, fix.accuracyM ?? 0);
        if (!moved && dt < HEARTBEAT_MS) return false;
      }
      last = fix;
      return true;
    },
    reset() {
      last = null;
    },
  };
}
